import { Download, RotateCcw } from 'lucide-react';

import {
  getDownloadResourceError,
  getDownloadSizeLabel,
} from '@/i18n/download-resource-copy';
import { DownloadProgressRing } from '@/components/download-manager/download-progress-ring';
import { DownloadStatusIcon } from '@/components/download-manager/download-status-icon';
import { HorizontalOverflowHoverReveal } from '@/components/ui/horizontal-overflow-hover-reveal';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import type { DownloadManagerCopy } from '@/i18n/download-manager-copy';
import {
  formatDownloadManagerTime,
  getDownloadManagerFileDisplay,
  getDownloadManagerRowAction,
  getDownloadManagerStartedAt,
  type DownloadManagerJob,
} from '@/lib/download-manager-state';
import { formatDownloadBytes } from '@/lib/downloads-page-state';

type DownloadManagerRowProps = {
  job: DownloadManagerJob;
  copy: DownloadManagerCopy;
  locale: string;
  isDeliveryLocked: boolean;
  isDownloading: boolean;
  isRetryLocked: boolean;
  isRetrying: boolean;
  onDownload: (job: DownloadManagerJob) => void;
  onRetry: (job: DownloadManagerJob) => void;
};

export function DownloadManagerRow({
  job,
  copy,
  locale,
  isDeliveryLocked,
  isDownloading,
  isRetryLocked,
  isRetrying,
  onDownload,
  onRetry,
}: DownloadManagerRowProps) {
  const isActive = job.status === 'queued' || job.status === 'running';
  const rowAction = getDownloadManagerRowAction(job);
  const statusText = copy[job.status];
  const fileDisplay = getDownloadManagerFileDisplay(job);
  const metadata = [
    statusText,
    job.status === 'failed' && !job.reasonText
      ? getDownloadResourceError(job.errorMessage, locale)
      : null,
    job.status !== 'ready' && job.estimatedSourceBytes !== undefined
      ? getDownloadSizeLabel(
          formatDownloadBytes(job.estimatedSourceBytes),
          job.unknownSizeItemCount,
          locale
        )
      : null,
    fileDisplay?.kind === 'progress'
      ? copy.fileProgress(fileDisplay.completed, fileDisplay.total)
      : fileDisplay?.kind === 'count'
        ? copy.fileCount(fileDisplay.count)
        : null,
    job.status === 'ready' && job.outputSizeBytes !== undefined
      ? formatDownloadBytes(job.outputSizeBytes)
      : null,
  ].filter((value) => value !== null);
  const startedAtTimestamp = getDownloadManagerStartedAt(job);
  const startedAt = formatDownloadManagerTime(startedAtTimestamp, locale);

  return (
    <div className="group/row flex min-h-13 items-center gap-2.5 px-2.5 py-2">
      {isActive ? (
        <div data-slot="download-manager-job-indicator" className="shrink-0">
          <DownloadProgressRing
            progress={job.progress}
            label={copy.itemProgress(job.archiveName, job.progress)}
            className="size-9 text-[9px]"
          />
        </div>
      ) : (
        <div className="bg-muted/60 flex size-9 shrink-0 items-center justify-center rounded-full">
          <span
            data-slot="download-manager-job-indicator"
            className="flex items-center justify-center"
          >
            <DownloadStatusIcon status={job.status} className="size-5" />
          </span>
        </div>
      )}

      <div className="max-w-full min-w-0 flex-1">
        <div data-slot="download-manager-row-title" className="min-w-0">
          <HorizontalOverflowHoverReveal
            className="w-full min-w-0"
            contentClassName="text-sm font-medium leading-tight"
          >
            <span title={job.archiveName}>{job.archiveName}</span>
          </HorizontalOverflowHoverReveal>
        </div>
        <p
          data-slot="download-manager-row-metadata"
          className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-[11px] leading-tight"
        >
          <span className="truncate" title={metadata.join(' · ')}>
            {metadata.join(' · ')}
          </span>
          <span aria-hidden="true" className="shrink-0">
            ·
          </span>
          <time
            dateTime={new Date(startedAtTimestamp).toISOString()}
            className="shrink-0 text-[11px] leading-tight tabular-nums"
          >
            {startedAt}
          </time>
        </p>
        {job.status === 'failed' && job.reasonText ? (
          // The reason is the point of a failed row: its own line, never
          // cut to one line (TASTE #6171).
          <p
            data-slot="download-manager-row-reason"
            className="type-meta text-muted-foreground mt-0.5 line-clamp-2 text-pretty"
          >
            {job.reasonText}
          </p>
        ) : null}
      </div>

      {rowAction === 'download' ? (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          disabled={isDeliveryLocked || isDownloading}
          className="hover:bg-accent/70 cursor-pointer rounded-full transition-[background-color,color,opacity,transform] duration-150 ease-out enabled:active:scale-[0.96] disabled:pointer-events-auto disabled:cursor-not-allowed motion-reduce:transition-none"
          aria-label={`${copy.download} ${job.archiveName}`}
          title={`${copy.download} ${job.archiveName}`}
          onClick={() => onDownload(job)}
        >
          {isDownloading ? <Loader size="sm" /> : <Download />}
        </Button>
      ) : rowAction === 'retry' ? (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          disabled={isRetryLocked}
          className="hover:bg-accent/70 cursor-pointer rounded-full transition-[background-color,color,opacity,transform] duration-150 ease-out enabled:active:scale-[0.96] disabled:pointer-events-auto disabled:cursor-not-allowed motion-reduce:transition-none"
          aria-label={`${copy.retry} ${job.archiveName}`}
          title={`${copy.retry} ${job.archiveName}`}
          onClick={() => onRetry(job)}
        >
          {isRetrying ? <Loader size="sm" /> : <RotateCcw />}
        </Button>
      ) : null}
    </div>
  );
}
